import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  api,
  FalhaApi,
  type AchadoAvulsoApi,
  type FotoApi,
  type PendenciaAnteriorApi,
  type SnapshotItem,
  type SnapshotSecao,
  type VistoriaApi,
} from "../api";
import { IndicadorSincronizacao } from "../componentes/IndicadorSincronizacao";
import { Layout } from "../componentes/Layout";
import { VisualizadorFoto } from "../componentes/VisualizadorFoto";
import { comprimirImagem } from "../offline/comprimirImagem";
import { PillStatusVistoria } from "../componentes/Pill";
import { useSessao } from "../estado/sessao";
import {
  IconeAvisoTriangulo,
  IconeCirculoCheck,
  IconeInfo,
  IconeInterrogacaoCirculo,
  IconeLixeira,
  IconePontos,
  IconeSetaEsquerda,
} from "../componentes/Icones";
import { offlineDb, type MutacaoFoto } from "../offline/db";
import {
  enfileirarAchadoAvulso,
  enfileirarCampo,
  enfileirarFoto,
  enfileirarObservacao,
  enfileirarResposta,
  eventosSincronizacao,
  useSincronizacao,
  type MutacaoAplicadaDetalhe,
  type MutacaoDescartadaDetalhe,
} from "../offline/sync";

// Foto ainda na fila offline, sem id de servidor — a miniatura usa o Blob local (object URL) em
// vez de bater na rota autenticada de arquivo, que não existiria ainda pra ela. Sem excluir/
// legenda enquanto pendente: não há fotoId real pra essas rotas mirarem.
interface FotoPendenteLocal {
  id: string; // = mutationId, funciona como key de UI até a foto real chegar
  secaoChave: string;
  itemChave: string | null;
  capturadaEmDispositivoEm: string | null;
  pendente: true;
  previewLocalUrl: string;
}

type FotoParaExibir = FotoApi | FotoPendenteLocal;

/**
 * Tela de preenchimento por seção. Fase 6 (offline-first): toda escrita passa pela fila em
 * IndexedDB (offline/sync.ts) em vez de chamar a API direto — o update de tela é sempre otimista
 * e imediato, a rede é só um detalhe de quando a mutação de fato sai do aparelho. A leitura
 * inicial também cai pra um cache local quando a rede falha, então abrir uma vistoria já visitada
 * antes continua funcionando sem sinal nenhum.
 *
 * `enterKeyHint="done"` em todo input/textarea de texto: sem isso, o teclado mobile oferece
 * "Seguir" (→), que pula pro PRÓXIMO <input>/<textarea> real do DOM — como as seções são um
 * acordeão (só a aberta renderiza seus campos) e os botões de foto/seção não contam como campo
 * de formulário, "Seguir" a partir do último campo de uma seção aberta (geralmente a Legenda de
 * uma foto) pula todas as seções fechadas de uma vez e cai direto em "Observações gerais",
 * no fim da página — bug real reportado pelo usuário no mobile.
 */
export function VistoriaDetalhe() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const { usuario } = useSessao();
  const [vistoria, setVistoria] = useState<VistoriaApi | null>(null);
  const [pendenciasAnteriores, setPendenciasAnteriores] = useState<PendenciaAnteriorApi[]>([]);
  const [usandoCache, setUsandoCache] = useState(false);
  const [fotosPendentes, setFotosPendentes] = useState<FotoPendenteLocal[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [concluindo, setConcluindo] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  // Levantado pro componente pai (em vez de estado local em cada SecaoFormulario) pra persistir
  // qual seção está aberta entre re-renders da lista inteira.
  const [secoesAbertas, setSecoesAbertas] = useState<Set<string>>(new Set());
  // Foto aberta em tela cheia (clique numa miniatura, em qualquer seção/item) — só uma por vez,
  // por isso fica aqui em vez de estado local em cada componente de galeria.
  const [fotoAmpliada, setFotoAmpliada] = useState<FotoParaExibir | null>(null);

  // Debounce de resposta por item (chave `secaoChave:itemChave`) — ver salvarResposta logo abaixo.
  // Um toque rápido em "Não conforme" seguido de correção em segundos gerava achado + tarefa só
  // pra cancelar tudo de novo na sequência (achado real, 21/08: 75% dos cancelamentos aconteciam
  // em até 5s da criação). A UI continua reagindo na hora (updates otimistas não mudam), só o
  // envio de verdade pro servidor espera o valor "assentar" — se vier outra mudança do mesmo item
  // antes do timer disparar, ela funde no payload pendente e reinicia a espera; se a correção
  // acontecer nesse intervalo, nada nunca chega a ser enviado, então nenhum achado chega a nascer.
  const debounceResposta = useRef<
    Map<
      string,
      {
        secaoChave: string;
        itemChave: string;
        valor: { marcado?: boolean; valorEscolhido?: string; descricao?: string; problema?: boolean };
        timer: ReturnType<typeof setTimeout>;
      }
    >
  >(new Map());
  // Sem isso, sair da tela (voltar pra lista, trocar de vistoria) no meio da espera perderia a
  // última mudança de verdade — nunca chegaria a ser enviada. Dispara tudo que ainda estava
  // pendente na saída, em vez de deixar o timer morrer junto com o componente.
  useEffect(() => {
    return () => {
      for (const pendente of debounceResposta.current.values()) {
        clearTimeout(pendente.timer);
        void enfileirarResposta(id!, { secaoChave: pendente.secaoChave, itemChave: pendente.itemChave, ...pendente.valor });
      }
      debounceResposta.current.clear();
    };
  }, [id]);

  function carregar() {
    if (!id) return;
    api
      .obterVistoria(id)
      .then(({ vistoria, pendenciasAnteriores }) => {
        setVistoria(vistoria);
        setPendenciasAnteriores(pendenciasAnteriores);
        setUsandoCache(false);
        void offlineDb.vistoriaCache.put({ id, dados: vistoria, atualizadoEm: new Date().toISOString() });
      })
      .catch(() => {
        void offlineDb.vistoriaCache.get(id).then((cache) => {
          if (cache) {
            setVistoria(cache.dados);
            setUsandoCache(true);
          } else {
            setErro("Falha ao carregar vistoria");
          }
        });
      });
  }

  // Sem window.confirm (trava a página em iOS Safari) — a confirmação é uma etapa inline própria
  // (botão "Excluir vistoria" vira um cartão "tem certeza?"), não um clique único direto como nos
  // outros "Excluir" do app: essa ação some a vistoria da lista principal do dia pra dia, e um
  // toque sem querer não pode fazer isso sem uma segunda confirmação explícita.
  async function excluirVistoria() {
    if (!id) return;
    setExcluindo(true);
    try { await api.excluirVistoria(id); navegar("/vistorias"); }
    catch (e) { setErro(e instanceof FalhaApi ? e.message : "Não foi possível excluir a vistoria"); setExcluindo(false); }
  }

  useEffect(carregar, [id]);

  // Fase 8: o PDF é gerado por um worker em segundo plano (cron de 1 min, ver
  // server/src/workers/agendador.ts), não na resposta de "Concluir vistoria" — poll leve só
  // enquanto a vistoria está concluída sem pdfPath ainda, pra trocar "Gerando PDF..." pelo link de
  // download sozinho, sem o usuário precisar recarregar a página manualmente.
  useEffect(() => {
    if (!vistoria || vistoria.status === "EM_ANDAMENTO" || vistoria.pdfPath) return;
    const intervalo = setInterval(carregar, 5000);
    return () => clearInterval(intervalo);
  }, [vistoria?.status, vistoria?.pdfPath]);

  // Escreve toda atualização otimista também no cache local — sem isso, um reload enquanto ainda
  // offline voltaria a mostrar a última versão que veio do servidor, "esquecendo" o que foi
  // editado nesse meio tempo (que continua seguro na fila, só não apareceria na tela até drenar).
  function atualizarVistoria(atualizar: (v: VistoriaApi) => VistoriaApi) {
    setVistoria((v) => {
      if (!v) return v;
      const nova = atualizar(v);
      void offlineDb.vistoriaCache.put({ id: nova.id, dados: nova, atualizadoEm: new Date().toISOString() });
      return nova;
    });
  }

  // Restaura fotos que ficaram na fila de uma sessão anterior (ex.: página recarregada ainda
  // offline) — sem isso o usuário não teria nenhum indício visual de que ainda há uma foto pra
  // enviar até a drenagem automática terminar sozinha em segundo plano.
  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    void offlineDb.mutacoesPendentes
      .where("vistoriaId")
      .equals(id)
      .toArray()
      .then((mutacoes) => {
        if (cancelado) return;
        const fotos = mutacoes.filter((m): m is MutacaoFoto => m.tipo === "foto");
        setFotosPendentes(
          fotos.map((m) => ({
            id: m.id,
            secaoChave: m.payload.secaoChave,
            itemChave: m.payload.itemChave ?? null,
            capturadaEmDispositivoEm: m.payload.capturadaEmDispositivoEm ?? null,
            pendente: true,
            previewLocalUrl: URL.createObjectURL(m.payload.arquivo),
          }))
        );
      });
    return () => {
      cancelado = true;
    };
  }, [id]);

  // Quando uma foto pendente termina de sincronizar (ou é definitivamente descartada depois de
  // esgotar as tentativas), troca o placeholder local pela foto real vinda do servidor.
  useEffect(() => {
    function aoAplicar(evento: Event) {
      const { detail } = evento as CustomEvent<MutacaoAplicadaDetalhe>;
      if (detail.vistoriaId !== id || detail.tipo !== "foto") return;
      setFotosPendentes((atuais) => {
        const alvo = atuais.find((f) => f.id === detail.mutationId);
        if (alvo) URL.revokeObjectURL(alvo.previewLocalUrl);
        return atuais.filter((f) => f.id !== detail.mutationId);
      });
      const resultado = detail.resultado as { foto: FotoApi } | undefined;
      if (resultado?.foto) {
        atualizarVistoria((v) => ({ ...v, fotos: [...v.fotos, resultado.foto] }));
      }
    }
    function aoDescartar(evento: Event) {
      const { detail } = evento as CustomEvent<MutacaoDescartadaDetalhe>;
      if (detail.vistoriaId !== id || detail.tipo !== "foto") return;
      setFotosPendentes((atuais) => {
        const alvo = atuais.find((f) => f.id === detail.mutationId);
        if (alvo) URL.revokeObjectURL(alvo.previewLocalUrl);
        return atuais.filter((f) => f.id !== detail.mutationId);
      });
      setErro(`Falha ao enviar uma foto: ${detail.erro}`);
    }
    eventosSincronizacao.addEventListener("aplicada", aoAplicar);
    eventosSincronizacao.addEventListener("descartada", aoDescartar);
    return () => {
      eventosSincronizacao.removeEventListener("aplicada", aoAplicar);
      eventosSincronizacao.removeEventListener("descartada", aoDescartar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // A fila esvaziar é o melhor sinal de que a rede voltou de verdade (algo acabou de ser enviado
  // com sucesso) — busca os dados atuais do servidor de novo pra tirar o aviso de "mostrando
  // dados salvos no aparelho", que senão ficaria preso até a próxima navegação manual.
  const { pendentes } = useSincronizacao();
  useEffect(() => {
    if (pendentes === 0 && usandoCache) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendentes]);

  if (!vistoria) {
    return (
      <Layout>
        <main className="container">
          {erro ? (
            <div className="alerta alerta-erro" role="alert">
              {erro}
            </div>
          ) : (
            <p>Carregando…</p>
          )}
        </main>
      </Layout>
    );
  }

  // Vistoria excluída (exclusão lógica) nunca é editável, mesmo que o status ainda diga
  // EM_ANDAMENTO — vira só histórico, só ADMIN chega até aqui pra ver.
  const editavel = vistoria.status === "EM_ANDAMENTO" && !vistoria.excluidaEm;

  const respostaPorChave = new Map(
    vistoria.respostas.map((r) => [`${r.secaoChave}:${r.itemChave}`, r])
  );
  const observacaoPorSecao = new Map(vistoria.observacoesSecao.map((o) => [o.secaoChave, o.texto]));

  const achadosAvulsosPorSecao = new Map<string, AchadoAvulsoApi[]>();
  for (const achado of vistoria.achadosAvulsos) {
    const lista = achadosAvulsosPorSecao.get(achado.secaoChave) ?? [];
    lista.push(achado);
    achadosAvulsosPorSecao.set(achado.secaoChave, lista);
  }

  // Fotos de nível de seção (item CONFORME é o único caso com fotos por item, na caixa de não
  // conformidade — as demais telas continuam anexando foto à seção como um todo).
  const fotosPorSecao = new Map<string, FotoParaExibir[]>();
  const fotosPorItem = new Map<string, FotoParaExibir[]>();
  for (const foto of [...vistoria.fotos, ...fotosPendentes]) {
    if (foto.itemChave) {
      const chave = `${foto.secaoChave}:${foto.itemChave}`;
      const lista = fotosPorItem.get(chave) ?? [];
      lista.push(foto);
      fotosPorItem.set(chave, lista);
      continue;
    }
    const lista = fotosPorSecao.get(foto.secaoChave) ?? [];
    lista.push(foto);
    fotosPorSecao.set(foto.secaoChave, lista);
  }

  async function salvarCampo(campo: string, valor: string) {
    if (!vistoria) return;
    atualizarVistoria((v) => ({ ...v, [campo]: valor }));
    try {
      await enfileirarCampo(vistoria.id, { [campo]: valor });
    } catch {
      setErro("Falha ao salvar — tente novamente");
    }
  }

  async function concluir() {
    if (!vistoria) return;
    setConcluindo(true);
    setErro(null);
    try {
      const { vistoria: atualizada } = await api.concluirVistoria(vistoria.id);
      setVistoria(atualizada);
      void offlineDb.vistoriaCache.put({ id: atualizada.id, dados: atualizada, atualizadoEm: new Date().toISOString() });
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao concluir vistoria — tente novamente");
    } finally {
      setConcluindo(false);
    }
  }

  async function salvarResposta(
    item: SnapshotItem,
    secaoChave: string,
    valor: { marcado?: boolean; valorEscolhido?: string; descricao?: string; problema?: boolean }
  ) {
    if (!vistoria) return;
    const existente = vistoria.respostas.find((r) => r.secaoChave === secaoChave && r.itemChave === item.chave);
    const respostaOtimista = {
      id: existente?.id ?? `local-${item.chave}`,
      secaoChave,
      itemChave: item.chave,
      marcado: valor.marcado !== undefined ? valor.marcado : (existente?.marcado ?? null),
      valorEscolhido: valor.valorEscolhido !== undefined ? valor.valorEscolhido : (existente?.valorEscolhido ?? null),
      descricaoNaoConformidade:
        valor.descricao !== undefined ? valor.descricao || null : (existente?.descricaoNaoConformidade ?? null),
      problemaIdentificado: valor.problema !== undefined ? valor.problema : (existente?.problemaIdentificado ?? null),
    };
    atualizarVistoria((v) => ({
      ...v,
      respostas: [
        ...v.respostas.filter((r) => !(r.secaoChave === secaoChave && r.itemChave === item.chave)),
        respostaOtimista,
      ],
    }));

    const chave = `${secaoChave}:${item.chave}`;
    const pendente = debounceResposta.current.get(chave);
    if (pendente) clearTimeout(pendente.timer);
    const valorFundido = { ...pendente?.valor, ...valor };
    const timer = setTimeout(() => {
      debounceResposta.current.delete(chave);
      enfileirarResposta(vistoria.id, { secaoChave, itemChave: item.chave, ...valorFundido }).catch(() => {
        setErro("Falha ao salvar resposta — tente novamente");
      });
    }, 10000);
    debounceResposta.current.set(chave, { secaoChave, itemChave: item.chave, valor: valorFundido, timer });
  }

  // Lixeira da caixa de não conformidade: volta o item CONFORME pro estado "sem resposta" (em vez
  // de só trocar pra "Conforme", que seria uma afirmação que ninguém verificou de fato).
  async function limparResposta(item: SnapshotItem, secaoChave: string) {
    if (!vistoria) return;
    atualizarVistoria((v) => ({
      ...v,
      respostas: v.respostas.filter((r) => !(r.secaoChave === secaoChave && r.itemChave === item.chave)),
    }));
    // Sem isso, um "Não conforme" ainda represado no debounce (ver salvarResposta) dispararia
    // sozinho alguns segundos depois e ressuscitaria a resposta que acabou de ser limpa aqui.
    const chave = `${secaoChave}:${item.chave}`;
    const pendente = debounceResposta.current.get(chave);
    if (pendente) {
      clearTimeout(pendente.timer);
      debounceResposta.current.delete(chave);
    }
    try {
      await enfileirarResposta(vistoria.id, { secaoChave, itemChave: item.chave, valorEscolhido: "", descricao: "" });
    } catch {
      setErro("Falha ao limpar resposta — tente novamente");
    }
  }

  async function salvarObservacao(secaoChave: string, texto: string) {
    if (!vistoria) return;
    const existente = vistoria.observacoesSecao.find((o) => o.secaoChave === secaoChave);
    const observacaoOtimista = { id: existente?.id ?? `local-${secaoChave}`, secaoChave, texto };
    atualizarVistoria((v) => ({
      ...v,
      observacoesSecao: [
        ...v.observacoesSecao.filter((o) => o.secaoChave !== secaoChave),
        observacaoOtimista,
      ],
    }));
    try {
      await enfileirarObservacao(vistoria.id, { secaoChave, texto });
    } catch {
      setErro("Falha ao salvar observação — tente novamente");
    }
  }

  // itemChave gerado aqui mesmo (não vem do servidor) — é o que permite o card aparecer na hora,
  // sem depender de rede, e a edição de descrição logo em seguida já ter uma chave estável pra
  // mirar (mesmo id que a mutação de criação, upsert idempotente do lado do servidor).
  async function adicionarAchadoAvulso(secaoChave: string) {
    if (!vistoria) return;
    const itemChave = crypto.randomUUID();
    atualizarVistoria((v) => ({
      ...v,
      achadosAvulsos: [...v.achadosAvulsos, { id: `local-${itemChave}`, secaoChave, itemChave, descricao: null }],
    }));
    try {
      await enfileirarAchadoAvulso(vistoria.id, { secaoChave, itemChave, descricao: "" });
    } catch {
      setErro("Falha ao adicionar achado — tente novamente");
    }
  }

  async function salvarDescricaoAchadoAvulso(secaoChave: string, itemChave: string, descricao: string) {
    if (!vistoria) return;
    atualizarVistoria((v) => ({
      ...v,
      achadosAvulsos: v.achadosAvulsos.map((a) => (a.itemChave === itemChave ? { ...a, descricao } : a)),
    }));
    try {
      await enfileirarAchadoAvulso(vistoria.id, { secaoChave, itemChave, descricao });
    } catch {
      setErro("Falha ao salvar achado — tente novamente");
    }
  }

  // Exclusão de verdade — não passa pela fila offline (exige rede), mesmo tratamento que
  // excluirFoto: só tira da tela depois de confirmado no servidor.
  async function excluirAchadoAvulso(itemChave: string) {
    if (!vistoria) return;
    try {
      await api.excluirAchadoAvulso(vistoria.id, itemChave);
      atualizarVistoria((v) => ({ ...v, achadosAvulsos: v.achadosAvulsos.filter((a) => a.itemChave !== itemChave) }));
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao excluir achado");
    }
  }

  async function enviarFotos(secaoChave: string, arquivos: FileList, itemChave?: string) {
    if (!vistoria) return;
    const agora = new Date().toISOString();
    for (const arquivoOriginal of Array.from(arquivos)) {
      // Comprime ANTES de enfileirar — é o Blob comprimido que fica gravado no IndexedDB e sai
      // no upload, não o arquivo cru da câmera/galeria (ver offline/comprimirImagem.ts).
      const arquivo = await comprimirImagem(arquivoOriginal).catch(() => arquivoOriginal);
      const nomeArquivo = arquivoOriginal.name.replace(/\.\w+$/, "") + ".jpg";
      const previewLocalUrl = URL.createObjectURL(arquivo);
      try {
        const mutationId = await enfileirarFoto(vistoria.id, {
          arquivo,
          nomeArquivo,
          secaoChave,
          itemChave,
          capturadaEmDispositivoEm: agora,
        });
        setFotosPendentes((atuais) => [
          ...atuais,
          {
            id: mutationId,
            secaoChave,
            itemChave: itemChave ?? null,
            capturadaEmDispositivoEm: agora,
            pendente: true,
            previewLocalUrl,
          },
        ]);
      } catch {
        URL.revokeObjectURL(previewLocalUrl);
        setErro("Falha ao enfileirar foto — tente novamente");
      }
    }
  }

  async function excluirFoto(fotoId: string) {
    if (!vistoria) return;
    try {
      await api.excluirFoto(vistoria.id, fotoId);
      atualizarVistoria((v) => ({ ...v, fotos: v.fotos.filter((f) => f.id !== fotoId) }));
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao excluir foto");
    }
  }

  async function editarLegendaFoto(fotoId: string, legenda: string) {
    if (!vistoria) return;
    try {
      const { foto } = await api.editarFoto(vistoria.id, fotoId, { legenda });
      atualizarVistoria((v) => ({ ...v, fotos: v.fotos.map((f) => (f.id === fotoId ? foto : f)) }));
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao salvar legenda");
    }
  }

  function alternarSecao(secaoChave: string) {
    setSecoesAbertas((atuais) => {
      const novas = new Set(atuais);
      if (novas.has(secaoChave)) novas.delete(secaoChave);
      else novas.add(secaoChave);
      return novas;
    });
  }

  // Resumo Conformes/Não conformes/N.A. do topo — só considera itens do tipo CONFORME; templates
  // que não usam esse tipo de item mostram tudo zerado e a barra fixa/resumo somem (ver JSX).
  let totalItensConforme = 0;
  let totalConformes = 0;
  let totalNaoConformes = 0;
  let totalNA = 0;
  let totalItensGeral = 0;
  let itensRespondidosGeral = 0;
  for (const secao of vistoria.templateSnapshot.secoes) {
    for (const item of secao.itens) {
      totalItensGeral++;
      const resposta = respostaPorChave.get(`${secao.chave}:${item.chave}`);
      if (resposta && (resposta.marcado !== null || (resposta.valorEscolhido ?? "") !== "")) {
        itensRespondidosGeral++;
      }
      if (item.tipo !== "CONFORME") continue;
      totalItensConforme++;
      if (resposta?.valorEscolhido === "Conforme") totalConformes++;
      else if (resposta?.valorEscolhido === "Não conforme") totalNaoConformes++;
      else if (resposta?.valorEscolhido === "N.A.") totalNA++;
    }
  }
  const progressoPercentual = totalItensGeral > 0 ? Math.round((itensRespondidosGeral / totalItensGeral) * 100) : 0;

  return (
    <>
    <Layout>
      <main
        className={`container${editavel && totalItensConforme > 0 ? " espaco-barra-fixa" : ""}`}
        style={{ maxWidth: 720 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <button
            type="button"
            onClick={() => navegar("/vistorias")}
            aria-label="Voltar"
            style={{ background: "none", border: "none", padding: 4, marginLeft: -4, cursor: "pointer", color: "var(--texto)" }}
          >
            <IconeSetaEsquerda tamanho={22} />
          </button>
          <h2 style={{ flex: 1, minWidth: 0 }}>{vistoria.cliente.nome}</h2>
          <IconePontos tamanho={20} cor="var(--texto-suave)" />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "0 0 12px",
            color: "var(--texto-suave)",
            fontSize: 14,
          }}
        >
          <span>
            {vistoria.cliente.administradora ? `${vistoria.cliente.administradora} · ` : ""}
            {new Date(vistoria.dataVistoria).toLocaleDateString("pt-BR", { timeZone: "UTC" })}
          </span>
          <PillStatusVistoria status={vistoria.status} />
        </div>

        {totalItensGeral > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div className="barra-progresso-legenda">
              <span>
                {itensRespondidosGeral}/{totalItensGeral} itens
              </span>
              <span>{progressoPercentual}%</span>
            </div>
            <div className="barra-progresso-trilho">
              <div className="barra-progresso-preenchimento" style={{ width: `${progressoPercentual}%` }} />
            </div>
          </div>
        )}

        {totalItensConforme > 0 && (
          <div className="cartao grade-resumo">
            <div className="tile-resumo">
              <span className="tile-resumo-selo" style={{ background: "var(--pill-verde-fundo)" }}>
                <IconeCirculoCheck tamanho={20} cor="var(--pill-verde-texto)" selecionado />
              </span>
              <div className="tile-resumo-texto">
                <strong>{totalConformes}</strong>
                <span>Conformes</span>
              </div>
            </div>
            <div className="tile-resumo">
              <span className="tile-resumo-selo" style={{ background: "var(--pill-ambar-fundo)" }}>
                <IconeAvisoTriangulo tamanho={20} cor="var(--pill-ambar-texto)" />
              </span>
              <div className="tile-resumo-texto">
                <strong>{totalNaoConformes}</strong>
                <span>Não conformes</span>
              </div>
            </div>
            <div className="tile-resumo">
              <span className="tile-resumo-selo" style={{ background: "var(--pill-neutro-fundo)" }}>
                <IconeInterrogacaoCirculo tamanho={20} cor="var(--pill-neutro-texto)" />
              </span>
              <div className="tile-resumo-texto">
                <strong>{totalNA}</strong>
                <span>N.A.</span>
              </div>
            </div>
          </div>
        )}

        <IndicadorSincronizacao />

        {vistoria.excluidaEm && (
          <div className="alerta alerta-erro" role="alert">
            Vistoria excluída em {new Date(vistoria.excluidaEm).toLocaleDateString("pt-BR")}
            {vistoria.excluidaPor ? ` por ${vistoria.excluidaPor.nome}` : ""}. Fica visível só para
            administradores, como histórico — não pode mais ser editada.
          </div>
        )}

        {usandoCache && (
          <div className="alerta" style={{ background: "var(--fundo)", border: "1px solid var(--borda)", fontSize: 13 }}>
            📴 Mostrando dados salvos no aparelho (sem conexão no momento) — o que você editar aqui
            entra na fila e sincroniza sozinho quando a rede voltar.
          </div>
        )}

        {erro && (
          <div className="alerta alerta-erro" role="alert">
            {erro}
          </div>
        )}

        {pendenciasAnteriores.length > 0 && (
          <section className="pendencias-verificacao">
            <div className="pendencias-verificacao-cabecalho">
              <div>
                <span>ACOMPANHAMENTO</span>
                <h3>Pendências de vistorias anteriores</h3>
              </div>
              <strong>{pendenciasAnteriores.length}</strong>
            </div>
            <p className="pendencias-verificacao-intro">
              Confira no local se as correções abaixo foram realmente executadas antes de encerrar a pendência.
            </p>
            {pendenciasAnteriores.map((pendencia) => (
              <PendenciaAnteriorCard
                key={pendencia.id}
                pendencia={pendencia}
                vistoriaId={vistoria.id}
                editavel={editavel}
                aoVerificar={async (resultado, observacao) => {
                  setErro(null);
                  try {
                    await api.verificarNaoConformidade(vistoria.id, {
                      naoConformidadeId: pendencia.id,
                      resultado,
                      observacao,
                    });
                    carregar();
                  } catch (e) {
                    setErro(e instanceof FalhaApi ? e.message : "Falha ao registrar a verificação");
                    throw e;
                  }
                }}
              />
            ))}
          </section>
        )}

        <div className="cartao" style={{ padding: 16, marginBottom: 16 }}>
          <label htmlFor="responsavel">Responsável pela vistoria (presente no local, opcional)</label>
          <input
            id="responsavel"
            defaultValue={vistoria.responsavelNome ?? ""}
            disabled={!editavel}
            enterKeyHint="done"
            onBlur={(e) => salvarCampo("responsavelNome", e.target.value)}
          />
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="hora-inicio">Início</label>
              <input
                id="hora-inicio"
                type="time"
                defaultValue={vistoria.horarioInicio ?? ""}
                disabled={!editavel}
                onBlur={(e) => salvarCampo("horarioInicio", e.target.value)}
              />
            </div>
            {vistoria.horarioTermino && (
              <div style={{ flex: 1 }}>
                <label>Término</label>
                <div className="campo-somente-leitura">{vistoria.horarioTermino}</div>
              </div>
            )}
          </div>
        </div>

        {vistoria.templateSnapshot.secoes.map((secao) => (
          <SecaoFormulario
            key={secao.chave}
            vistoriaId={vistoria.id}
            secao={secao}
            editavel={editavel}
            aberta={secoesAbertas.has(secao.chave)}
            aoAlternar={() => alternarSecao(secao.chave)}
            respostaPorChave={respostaPorChave}
            observacao={observacaoPorSecao.get(secao.chave) ?? ""}
            fotos={fotosPorSecao.get(secao.chave) ?? []}
            fotosPorItem={fotosPorItem}
            achadosAvulsos={achadosAvulsosPorSecao.get(secao.chave) ?? []}
            aoSalvarResposta={salvarResposta}
            aoLimparResposta={limparResposta}
            aoSalvarObservacao={salvarObservacao}
            aoAdicionarAchadoAvulso={adicionarAchadoAvulso}
            aoSalvarDescricaoAchadoAvulso={salvarDescricaoAchadoAvulso}
            aoExcluirAchadoAvulso={excluirAchadoAvulso}
            aoEnviarFotos={enviarFotos}
            aoExcluirFoto={excluirFoto}
            aoEditarLegendaFoto={editarLegendaFoto}
            aoAbrirFoto={setFotoAmpliada}
          />
        ))}

        {editavel && totalItensConforme > 0 && (
          <div className="barra-acao-fixa">
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 14,
                fontWeight: 700,
                color: totalNaoConformes > 0 ? "var(--erro)" : "var(--texto-suave)",
              }}
            >
              {totalNaoConformes > 0 ? (
                <IconeAvisoTriangulo tamanho={18} cor="var(--erro)" />
              ) : (
                <IconeCirculoCheck tamanho={18} cor="var(--pill-verde-texto)" selecionado />
              )}
              {totalNaoConformes > 0
                ? `${totalNaoConformes} não conformidade${totalNaoConformes > 1 ? "s" : ""}`
                : "Sem não conformidades"}
            </span>
          </div>
        )}

        <div className="cartao" style={{ padding: 16, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Observações gerais</h3>
          <p style={{ margin: "-4px 0 12px", color: "var(--texto-suave)", fontSize: 14 }}>
            Registre uma síntese das condições encontradas durante a vistoria.
          </p>
          <textarea
            rows={5}
            defaultValue={vistoria.irregularidades ?? ""}
            disabled={!editavel}
            enterKeyHint="done"
            onBlur={(e) => salvarCampo("irregularidades", e.target.value)}
            placeholder="Resuma as condições encontradas, os principais problemas e os pontos que estavam em boas condições."
          />
        </div>

        {editavel ? (
          <button
            type="button"
            className="botao"
            style={{ width: "100%", marginBottom: 16 }}
            disabled={concluindo}
            onClick={concluir}
          >
            {concluindo ? "Concluindo…" : "Concluir vistoria"}
          </button>
        ) : (
          <div className="cartao" style={{ padding: 16, marginBottom: 16, textAlign: "center" }}>
            {vistoria.pdfPath ? (
              <a href={api.urlPdf(vistoria.id)} className="botao" style={{ display: "inline-block" }}>
                📄 Baixar PDF do relatório
              </a>
            ) : vistoria.excluidaEm ? (
              <p style={{ margin: 0, color: "var(--texto-suave)" }}>
                Vistoria excluída sem relatório gerado.
              </p>
            ) : (
              <p style={{ margin: 0, color: "var(--texto-suave)" }}>
                Vistoria concluída — gerando PDF do relatório… (atualiza sozinho)
              </p>
            )}
          </div>
        )}
        {usuario?.papel === "ADMIN" && !vistoria.excluidaEm && (
          confirmandoExclusao ? (
            <div className="cartao" style={{ padding: 14, marginTop: 8, border: "1px solid var(--erro)" }}>
              <p style={{ margin: "0 0 10px", fontSize: 14 }}>
                Excluir esta vistoria? Ela sai das listas normais e fica visível só como histórico
                para administradores — não pode ser desfeito por aqui.
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="botao"
                  style={{ marginTop: 0, background: "var(--erro)", borderColor: "var(--erro)" }}
                  disabled={excluindo}
                  onClick={() => void excluirVistoria()}
                >
                  {excluindo ? "Excluindo…" : "Sim, excluir"}
                </button>
                <button type="button" className="botao botao-texto" disabled={excluindo} onClick={() => setConfirmandoExclusao(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="botao botao-texto" style={{ marginTop: 8, color: "var(--erro)" }} onClick={() => setConfirmandoExclusao(true)}>
              Excluir vistoria
            </button>
          )
        )}
      </main>
    </Layout>
    {fotoAmpliada && (
      <VisualizadorFoto
        src={
          "pendente" in fotoAmpliada
            ? fotoAmpliada.previewLocalUrl
            : api.urlFoto(vistoria.id, fotoAmpliada.id)
        }
        urlDownload={"pendente" in fotoAmpliada ? null : api.urlFotoDownload(vistoria.id, fotoAmpliada.id)}
        onFechar={() => setFotoAmpliada(null)}
      />
    )}
    </>
  );
}

function PendenciaAnteriorCard({
  pendencia,
  vistoriaId,
  editavel,
  aoVerificar,
}: {
  pendencia: PendenciaAnteriorApi;
  vistoriaId: string;
  editavel: boolean;
  aoVerificar: (resultado: "RESOLVIDA" | "PERSISTE", observacao: string) => Promise<void>;
}) {
  const verificacaoAtual = pendencia.verificacoes.find((v) => v.vistoriaId === vistoriaId);
  const tarefa = pendencia.tarefas.find((t) => t.status === "AGUARDANDO_VERIFICACAO") ?? pendencia.tarefas[0];
  const [observacao, setObservacao] = useState(verificacaoAtual?.observacao ?? "");
  const [salvando, setSalvando] = useState(false);

  async function verificar(resultado: "RESOLVIDA" | "PERSISTE") {
    setSalvando(true);
    try {
      await aoVerificar(resultado, observacao);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <article className="pendencia-verificacao-card">
      <div className="pendencia-verificacao-topo">
        <div>
          <small>{pendencia.secaoTitulo}</small>
          <h4>{pendencia.itemRotulo}</h4>
        </div>
        <span className="pill pill-ambar">Pendente</span>
      </div>
      {pendencia.descricao && <p>{pendencia.descricao}</p>}
      <div className="pendencia-verificacao-origem">
        Identificada em {new Date(pendencia.vistoria.dataVistoria).toLocaleDateString("pt-BR", { timeZone: "UTC" })}
      </div>
      {tarefa && (
        <div className="pendencia-verificacao-tarefa">
          <strong>Correção solicitada</strong>
          <span>{tarefa.titulo}</span>
          {tarefa.responsavelUsuario && <small>Responsável: {tarefa.responsavelUsuario.nome}</small>}
          {tarefa.evidenciaDescricao && (
            <blockquote><strong>Evidência da execução:</strong> {tarefa.evidenciaDescricao}</blockquote>
          )}
        </div>
      )}
      {verificacaoAtual && (
        <div className={`alerta ${verificacaoAtual.resultado === "RESOLVIDA" ? "alerta-sucesso" : "alerta-aviso"}`}>
          Verificada nesta vistoria: {verificacaoAtual.resultado === "RESOLVIDA" ? "correção confirmada" : "problema ainda persiste"}.
        </div>
      )}
      {editavel && (
        <div className="pendencia-verificacao-acoes">
          <label htmlFor={`verificacao-${pendencia.id}`}>Observação da verificação</label>
          <textarea
            id={`verificacao-${pendencia.id}`}
            rows={2}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Descreva o que foi conferido no local"
          />
          <div>
            <button type="button" className="botao botao-secundario" disabled={salvando} onClick={() => void verificar("PERSISTE")}>
              Ainda persiste
            </button>
            <button type="button" className="botao botao-primario" disabled={salvando} onClick={() => void verificar("RESOLVIDA")}>
              Correção confirmada
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

function SecaoFormulario({
  vistoriaId,
  secao,
  editavel,
  aberta,
  aoAlternar,
  respostaPorChave,
  observacao,
  fotos,
  fotosPorItem,
  achadosAvulsos,
  aoSalvarResposta,
  aoLimparResposta,
  aoSalvarObservacao,
  aoAdicionarAchadoAvulso,
  aoSalvarDescricaoAchadoAvulso,
  aoExcluirAchadoAvulso,
  aoEnviarFotos,
  aoExcluirFoto,
  aoEditarLegendaFoto,
  aoAbrirFoto,
}: {
  vistoriaId: string;
  secao: SnapshotSecao;
  editavel: boolean;
  aberta: boolean;
  aoAlternar: () => void;
  respostaPorChave: Map<string, { marcado: boolean | null; valorEscolhido: string | null; descricaoNaoConformidade?: string | null; problemaIdentificado?: boolean | null }>;
  observacao: string;
  fotos: FotoParaExibir[];
  fotosPorItem: Map<string, FotoParaExibir[]>;
  achadosAvulsos: AchadoAvulsoApi[];
  aoSalvarResposta: (
    item: SnapshotItem,
    secaoChave: string,
    valor: { marcado?: boolean; valorEscolhido?: string; descricao?: string; problema?: boolean }
  ) => void;
  aoLimparResposta: (item: SnapshotItem, secaoChave: string) => void;
  aoSalvarObservacao: (secaoChave: string, texto: string) => void;
  aoAdicionarAchadoAvulso: (secaoChave: string) => void;
  aoSalvarDescricaoAchadoAvulso: (secaoChave: string, itemChave: string, descricao: string) => void;
  aoExcluirAchadoAvulso: (itemChave: string) => void;
  aoEnviarFotos: (secaoChave: string, arquivos: FileList, itemChave?: string) => void;
  aoExcluirFoto: (fotoId: string) => void;
  aoEditarLegendaFoto: (fotoId: string, legenda: string) => void;
  aoAbrirFoto: (foto: FotoParaExibir) => void;
}) {
  // Colapsada por padrão ajuda a navegar em vistorias com muitas seções (24, no template
  // padrão) sem precisar rolar por tudo no mobile — só a seção atual fica aberta por vez,
  // as demais mostram um resumo rápido (quantos itens já têm resposta) na barra fechada.
  // Estado levantado pro VistoriaDetalhe (ver aberta/aoAlternar) — "Ver não conformidades" precisa
  // conseguir abrir seções de fora.
  const totalItens = secao.itens.length;
  const itensRespondidos = secao.itens.filter((item) => {
    const r = respostaPorChave.get(`${secao.chave}:${item.chave}`);
    return r && (r.marcado !== null || (r.valorEscolhido ?? "") !== "");
  }).length;

  const itensConforme = secao.itens.filter((item) => item.tipo === "CONFORME");
  const naoConformidades = itensConforme.filter(
    (item) => respostaPorChave.get(`${secao.chave}:${item.chave}`)?.valorEscolhido === "Não conforme"
  ).length;

  const inputCamera = useRef<HTMLInputElement>(null);
  const inputGaleria = useRef<HTMLInputElement>(null);

  return (
    <div id={`secao-${secao.chave}`} className="cartao" style={{ padding: 16, marginBottom: 16 }}>
      <button
        type="button"
        onClick={aoAlternar}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          background: "none",
          border: "none",
          padding: 0,
          margin: 0,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <strong>{secao.titulo}</strong>
          {!aberta &&
            (itensConforme.length > 0 ? (
              <span className={`pill ${naoConformidades > 0 ? "pill-vermelho" : "pill-verde"}`}>
                {naoConformidades > 0 ? `${naoConformidades} não conformidade${naoConformidades > 1 ? "s" : ""}` : "0 não conformidades"}
              </span>
            ) : (
              <span className={`pill ${itensRespondidos === totalItens ? "pill-verde" : "pill-neutro"}`}>
                {itensRespondidos}/{totalItens}
                {fotos.length > 0 ? ` · 📷 ${fotos.length}` : ""}
              </span>
            ))}
        </span>
        <span style={{ fontSize: 18, color: "var(--texto-suave)" }}>{aberta ? "▲" : "▼"}</span>
      </button>

      {aberta && (
        <>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            {secao.itens.map((item) => {
          const resposta = respostaPorChave.get(`${secao.chave}:${item.chave}`);

          if (item.tipo === "CHECKBOX_SIMPLES") {
            if (item.avaliacao?.modo === "OPCOES") {
              return <div key={item.chave} className="item-avaliavel">
                <span style={{ fontSize: 14, display: "block", marginBottom: 5 }}>{item.rotulo}</span>
                <div className="resposta-sim-nao">
                  <label><input type="radio" name={`${secao.chave}-${item.chave}`} checked={resposta?.marcado === true} disabled={!editavel} onChange={() => aoSalvarResposta(item, secao.chave, { marcado: true })} /> Sim</label>
                  <label><input type="radio" name={`${secao.chave}-${item.chave}`} checked={resposta?.marcado === false} disabled={!editavel} onChange={() => aoSalvarResposta(item, secao.chave, { marcado: false })} /> Não</label>
                </div>
                <ControleProblema item={item} secaoChave={secao.chave} resposta={resposta} fotos={fotosPorItem.get(`${secao.chave}:${item.chave}`) ?? []} vistoriaId={vistoriaId} editavel={editavel} aoSalvarResposta={aoSalvarResposta} aoEnviarFotos={aoEnviarFotos} aoExcluirFoto={aoExcluirFoto} aoAbrirFoto={aoAbrirFoto} />
              </div>;
            }
            return (
              <div key={item.chave} className="item-avaliavel">
              <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={resposta?.marcado ?? false}
                  disabled={!editavel}
                  onChange={(e) => aoSalvarResposta(item, secao.chave, { marcado: e.target.checked })}
                />
                <span style={{ fontSize: 14, fontWeight: 400, textTransform: "none" }}>{item.rotulo}</span>
              </label>
              <ControleProblema item={item} secaoChave={secao.chave} resposta={resposta} fotos={fotosPorItem.get(`${secao.chave}:${item.chave}`) ?? []} vistoriaId={vistoriaId} editavel={editavel} aoSalvarResposta={aoSalvarResposta} aoEnviarFotos={aoEnviarFotos} aoExcluirFoto={aoExcluirFoto} aoAbrirFoto={aoAbrirFoto} />
              </div>
            );
          }

          // SIM_NAO é um STATUS_MULTIPLO com as opções fixas em "Sim"/"Não" — sem opcoes
          // configuráveis no template (TemplateDetalhe.tsx não pede, sempre undefined no
          // snapshot), então o rótulo vem hardcoded aqui em vez de vir de item.opcoes.
          if (item.tipo === "STATUS_MULTIPLO" || item.tipo === "SIM_NAO") {
            const opcoes = item.tipo === "SIM_NAO" ? ["Sim", "Não"] : (item.opcoes ?? []);
            return (
              <div key={item.chave} className="item-avaliavel">
                <span style={{ fontSize: 14, display: "block", marginBottom: 4 }}>{item.rotulo}</span>
                <div style={{ display: "flex", gap: 16 }}>
                  {opcoes.map((opcao) => (
                    <label key={opcao} style={{ display: "flex", alignItems: "center", gap: 4, margin: 0 }}>
                      <input
                        type="radio"
                        style={{ width: "auto" }}
                        name={`${secao.chave}-${item.chave}`}
                        checked={resposta?.valorEscolhido === opcao}
                        disabled={!editavel}
                        onChange={() => aoSalvarResposta(item, secao.chave, { valorEscolhido: opcao })}
                      />
                      <span style={{ fontSize: 13, fontWeight: 400, textTransform: "none" }}>{opcao}</span>
                    </label>
                  ))}
                </div>
                <ControleProblema item={item} secaoChave={secao.chave} resposta={resposta} fotos={fotosPorItem.get(`${secao.chave}:${item.chave}`) ?? []} vistoriaId={vistoriaId} editavel={editavel} aoSalvarResposta={aoSalvarResposta} aoEnviarFotos={aoEnviarFotos} aoExcluirFoto={aoExcluirFoto} aoAbrirFoto={aoAbrirFoto} />
              </div>
            );
          }

          if (item.tipo === "CONFORME") {
            return (
              <ItemConforme
                key={item.chave}
                vistoriaId={vistoriaId}
                item={item}
                secaoChave={secao.chave}
                resposta={resposta}
                fotos={fotosPorItem.get(`${secao.chave}:${item.chave}`) ?? []}
                editavel={editavel}
                aoSalvarResposta={aoSalvarResposta}
                aoLimparResposta={aoLimparResposta}
                aoEnviarFotos={aoEnviarFotos}
                aoExcluirFoto={aoExcluirFoto}
                aoAbrirFoto={aoAbrirFoto}
              />
            );
          }

          // TEXTO_LIVRE
          return (
            <div key={item.chave} className="item-avaliavel">
              <label htmlFor={`${secao.chave}-${item.chave}`}>{item.rotulo}</label>
              <input
                id={`${secao.chave}-${item.chave}`}
                defaultValue={resposta?.valorEscolhido ?? ""}
                disabled={!editavel}
                enterKeyHint="done"
                onBlur={(e) => aoSalvarResposta(item, secao.chave, { valorEscolhido: e.target.value })}
              />
              <ControleProblema item={item} secaoChave={secao.chave} resposta={resposta} fotos={fotosPorItem.get(`${secao.chave}:${item.chave}`) ?? []} vistoriaId={vistoriaId} editavel={editavel} aoSalvarResposta={aoSalvarResposta} aoEnviarFotos={aoEnviarFotos} aoExcluirFoto={aoExcluirFoto} aoAbrirFoto={aoAbrirFoto} />
            </div>
          );
            })}
          </div>

          <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <label style={{ margin: 0 }}>Fora do checklist</label>
            {editavel && (
              <button
                type="button"
                className="botao botao-texto"
                style={{ padding: "2px 4px" }}
                onClick={() => aoAdicionarAchadoAvulso(secao.chave)}
              >
                + Adicionar
              </button>
            )}
          </div>
          {achadosAvulsos.length === 0 ? (
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--texto-suave)" }}>
              Notou algo que o checklist não previu? Adicione aqui pra não perder o registro.
            </p>
          ) : (
            achadosAvulsos.map((achado) => (
              <AchadoAvulsoCard
                key={achado.itemChave}
                vistoriaId={vistoriaId}
                secaoChave={secao.chave}
                achado={achado}
                fotos={fotosPorItem.get(`${secao.chave}:${achado.itemChave}`) ?? []}
                editavel={editavel}
                aoSalvarDescricao={aoSalvarDescricaoAchadoAvulso}
                aoExcluir={aoExcluirAchadoAvulso}
                aoEnviarFotos={aoEnviarFotos}
                aoExcluirFoto={aoExcluirFoto}
                aoAbrirFoto={aoAbrirFoto}
              />
            ))
          )}

          <label htmlFor={`obs-${secao.chave}`} style={{ marginTop: 12 }}>
            Observações
          </label>
          <textarea
            id={`obs-${secao.chave}`}
            rows={2}
            defaultValue={observacao}
            disabled={!editavel}
            enterKeyHint="done"
            onBlur={(e) => aoSalvarObservacao(secao.chave, e.target.value)}
          />

          <label style={{ marginTop: 12 }}>Fotos</label>
          {fotos.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
              {fotos.map((foto) => (
                <div key={foto.id} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <img
                      src={
                        "pendente" in foto
                          ? foto.previewLocalUrl
                          : api.urlFoto(vistoriaId, foto.id, "miniatura")
                      }
                      alt=""
                      onClick={() => aoAbrirFoto(foto)}
                      style={{
                        width: 72,
                        height: 72,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid var(--borda)",
                        cursor: "pointer",
                        opacity: "pendente" in foto ? 0.6 : 1,
                      }}
                    />
                    {editavel && !("pendente" in foto) && (
                      <button
                        type="button"
                        className="botao botao-texto"
                        onClick={() => aoExcluirFoto(foto.id)}
                        style={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          background: "var(--superficie)",
                          border: "1px solid var(--borda)",
                          borderRadius: "50%",
                          width: 22,
                          height: 22,
                          padding: 0,
                          lineHeight: "20px",
                          fontSize: 12,
                        }}
                        title="Excluir foto"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {"pendente" in foto ? (
                      <div style={{ fontSize: 12, color: "var(--texto-suave)", marginTop: 4 }}>
                        Enviando…
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 11, color: "var(--texto-suave)" }}>
                          {new Date(foto.capturadaEmDispositivoEm ?? foto.criadoEm).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        <input
                          defaultValue={foto.legenda ?? ""}
                          disabled={!editavel}
                          placeholder="Legenda (opcional)"
                          enterKeyHint="done"
                          style={{ marginTop: 2, fontSize: 13, padding: "6px 8px" }}
                          onBlur={(e) => {
                            if (e.target.value !== (foto.legenda ?? "")) {
                              aoEditarLegendaFoto(foto.id, e.target.value);
                            }
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {editavel && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <input
                ref={inputCamera}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                capture="environment"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) aoEnviarFotos(secao.chave, e.target.files);
                  e.target.value = "";
                }}
              />
              <input
                ref={inputGaleria}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) aoEnviarFotos(secao.chave, e.target.files);
                  e.target.value = "";
                }}
              />
              <button type="button" className="botao botao-texto" onClick={() => inputCamera.current?.click()}>
                📷 Tirar foto
              </button>
              <button type="button" className="botao botao-texto" onClick={() => inputGaleria.current?.click()}>
                🖼️ Galeria
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ControleProblema({ item, secaoChave, resposta, fotos, vistoriaId, editavel, aoSalvarResposta, aoEnviarFotos, aoExcluirFoto, aoAbrirFoto }: {
  item: SnapshotItem;
  secaoChave: string;
  resposta?: { marcado: boolean | null; valorEscolhido: string | null; descricaoNaoConformidade?: string | null; problemaIdentificado?: boolean | null };
  fotos: FotoParaExibir[];
  vistoriaId: string;
  editavel: boolean;
  aoSalvarResposta: (item: SnapshotItem, secaoChave: string, valor: { marcado?: boolean; valorEscolhido?: string; descricao?: string; problema?: boolean }) => void;
  aoEnviarFotos: (secaoChave: string, arquivos: FileList, itemChave?: string) => void;
  aoExcluirFoto: (fotoId: string) => void;
  aoAbrirFoto: (foto: FotoParaExibir) => void;
}) {
  const regra = item.avaliacao;
  if (!regra || regra.modo === "PADRAO") return null;
  const valor = item.tipo === "CHECKBOX_SIMPLES" ? (resposta?.marcado ? "Marcado" : "Desmarcado") : resposta?.valorEscolhido;
  const automatico = regra.modo === "OPCOES" && !!valor && regra.valoresProblema?.includes(valor);
  const ativo = regra.modo === "MARCACAO_MANUAL" ? resposta?.problemaIdentificado === true : automatico === true;

  return <div className={`vistoria-problema ${ativo ? "ativo" : ""}`}>
    {regra.modo === "MARCACAO_MANUAL" && <label>
      <input type="checkbox" checked={resposta?.problemaIdentificado ?? false} disabled={!editavel} onChange={(e) => aoSalvarResposta(item, secaoChave, { marcado: resposta?.marcado ?? undefined, valorEscolhido: resposta?.valorEscolhido ?? undefined, problema: e.target.checked })} />
      <span>Registrar esta resposta como problema</span>
    </label>}
    {ativo && <>
      <textarea rows={2} defaultValue={resposta?.descricaoNaoConformidade ?? ""} disabled={!editavel} placeholder="Descreva o problema encontrado" onBlur={(e) => aoSalvarResposta(item, secaoChave, { marcado: resposta?.marcado ?? undefined, valorEscolhido: resposta?.valorEscolhido ?? undefined, problema: regra.modo === "MARCACAO_MANUAL" ? true : undefined, descricao: e.target.value })} />
      <FotosEvidenciaItem vistoriaId={vistoriaId} secaoChave={secaoChave} itemChave={item.chave} fotos={fotos} editavel={editavel} aoEnviarFotos={aoEnviarFotos} aoExcluirFoto={aoExcluirFoto} aoAbrirFoto={aoAbrirFoto} />
    </>}
  </div>;
}

/** Evidências seguem o item que gerou a pendência, não a seção inteira. Assim o mesmo upload
 * funciona tanto para “Não conforme” quanto para qualquer valor negativo configurado no template. */
function FotosEvidenciaItem({ vistoriaId, secaoChave, itemChave, fotos, editavel, aoEnviarFotos, aoExcluirFoto, aoAbrirFoto }: {
  vistoriaId: string;
  secaoChave: string;
  itemChave: string;
  fotos: FotoParaExibir[];
  editavel: boolean;
  aoEnviarFotos: (secaoChave: string, arquivos: FileList, itemChave?: string) => void;
  aoExcluirFoto: (fotoId: string) => void;
  aoAbrirFoto: (foto: FotoParaExibir) => void;
}) {
  const inputArquivo = useRef<HTMLInputElement>(null);
  return <div className="evidencias-problema">
    <div>Fotos de evidência{fotos.length > 0 ? ` (${fotos.length})` : ""}</div>
    <div className="grade-fotos-item">
      {fotos.map((foto) => <div key={foto.id} className="foto-item-thumb">
        <img src={"pendente" in foto ? foto.previewLocalUrl : api.urlFoto(vistoriaId, foto.id, "miniatura")} alt="" onClick={() => aoAbrirFoto(foto)} style={{ opacity: "pendente" in foto ? 0.6 : 1 }} />
        {editavel && !("pendente" in foto) && <button type="button" onClick={() => aoExcluirFoto(foto.id)} title="Excluir foto" className="foto-evidencia-excluir">×</button>}
      </div>)}
      {editavel && <>
        <input ref={inputArquivo} type="file" accept="image/png,image/jpeg,image/webp" multiple style={{ display: "none" }} onChange={(e) => { if (e.target.files?.length) aoEnviarFotos(secaoChave, e.target.files, itemChave); e.target.value = ""; }} />
        <button type="button" className="foto-adicionar" onClick={() => inputArquivo.current?.click()}><span style={{ fontSize: 16 }}>+</span> Adicionar</button>
      </>}
    </div>
  </div>;
}

/**
 * Item tipo CONFORME: rótulo + 3 botões (Conforme/Não conforme/N.A.) — cada um assume a cor da
 * própria resposta quando selecionado. "Não conforme" abre a caixa vermelha com descrição + fotos
 * daquele item específico (ver fotosPorItem em VistoriaDetalhe — usa o mesmo `itemChave` que o
 * upload de foto já aceitava desde a Fase 7, só nunca tinha UI). Componente à parte (não inline no
 * .map de SecaoFormulario) porque precisa de useRef pro input de arquivo — hooks não podem viver
 * dentro de um callback de .map.
 */
function ItemConforme({
  vistoriaId,
  item,
  secaoChave,
  resposta,
  fotos,
  editavel,
  aoSalvarResposta,
  aoLimparResposta,
  aoEnviarFotos,
  aoExcluirFoto,
  aoAbrirFoto,
}: {
  vistoriaId: string;
  item: SnapshotItem;
  secaoChave: string;
  resposta?: { marcado: boolean | null; valorEscolhido: string | null; descricaoNaoConformidade?: string | null };
  fotos: FotoParaExibir[];
  editavel: boolean;
  aoSalvarResposta: (
    item: SnapshotItem,
    secaoChave: string,
    valor: { marcado?: boolean; valorEscolhido?: string; descricao?: string }
  ) => void;
  aoLimparResposta: (item: SnapshotItem, secaoChave: string) => void;
  aoEnviarFotos: (secaoChave: string, arquivos: FileList, itemChave?: string) => void;
  aoExcluirFoto: (fotoId: string) => void;
  aoAbrirFoto: (foto: FotoParaExibir) => void;
}) {
  const inputArquivo = useRef<HTMLInputElement>(null);
  const naoConforme = resposta?.valorEscolhido === "Não conforme";

  return (
    <div>
      <span style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        {item.rotulo}
        <IconeInfo tamanho={15} cor="var(--texto-suave)" />
      </span>
      <div className="segmentado">
        {(
          [
            { valor: "Conforme", classe: "segmento-conforme", cor: "var(--pill-verde-texto)" },
            { valor: "Não conforme", classe: "segmento-nao-conforme", cor: "var(--pill-vermelho-texto)" },
            { valor: "N.A.", classe: "segmento-na", cor: "var(--texto)" },
          ] as const
        ).map((opcao) => {
          const selecionado = resposta?.valorEscolhido === opcao.valor;
          return (
            <button
              key={opcao.valor}
              type="button"
              disabled={!editavel}
              className={`segmento ${opcao.classe}${selecionado ? " segmento-selecionado" : ""}`}
              onClick={() => aoSalvarResposta(item, secaoChave, { valorEscolhido: opcao.valor })}
            >
              <IconeCirculoCheck tamanho={18} cor={selecionado ? opcao.cor : "var(--borda)"} selecionado={selecionado} />
              {opcao.valor}
            </button>
          );
        })}
      </div>

      {naoConforme && (
        <div className="caixa-nao-conformidade">
          <div className="caixa-nao-conformidade-cabecalho">
            <span className="caixa-nao-conformidade-rotulo">Não conformidade</span>
            {editavel && (
              <button
                type="button"
                className="botao botao-texto"
                style={{ color: "var(--erro)", padding: "2px 4px" }}
                title="Limpar resposta"
                onClick={() => aoLimparResposta(item, secaoChave)}
              >
                <IconeLixeira tamanho={17} cor="var(--erro)" />
              </button>
            )}
          </div>
          <textarea
            rows={2}
            defaultValue={resposta?.descricaoNaoConformidade ?? ""}
            disabled={!editavel}
            enterKeyHint="done"
            placeholder="Descrever a não conformidade encontrada"
            onBlur={(e) => aoSalvarResposta(item, secaoChave, { valorEscolhido: "Não conforme", descricao: e.target.value })}
          />

          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--texto-suave)", marginTop: 10 }}>
            Fotos{fotos.length > 0 ? ` (${fotos.length})` : ""}
          </div>
          <div className="grade-fotos-item">
            {fotos.map((foto) => (
              <div key={foto.id} className="foto-item-thumb">
                <img
                  src={"pendente" in foto ? foto.previewLocalUrl : api.urlFoto(vistoriaId, foto.id, "miniatura")}
                  alt=""
                  onClick={() => aoAbrirFoto(foto)}
                  style={{ opacity: "pendente" in foto ? 0.6 : 1 }}
                />
                {editavel && !("pendente" in foto) && (
                  <button
                    type="button"
                    onClick={() => aoExcluirFoto(foto.id)}
                    title="Excluir foto"
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      background: "rgba(30,30,36,0.6)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: 18,
                      height: 18,
                      lineHeight: "16px",
                      fontSize: 11,
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {editavel && (
              <>
                <input
                  ref={inputArquivo}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) aoEnviarFotos(secaoChave, e.target.files, item.chave);
                    e.target.value = "";
                  }}
                />
                <button type="button" className="foto-adicionar" onClick={() => inputArquivo.current?.click()}>
                  <span style={{ fontSize: 16 }}>+</span>
                  Adicionar
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Card de "Fora do checklist": mesma caixa vermelha do ItemConforme (descrição + fotos), mas sem
 * item de template por trás — cada card É o registro (não uma resposta que pode virar achado ou
 * não). A lixeira aqui exclui de verdade (ver excluirAchadoAvulso em VistoriaDetalhe), diferente
 * da lixeira do ItemConforme que só volta o item pro estado neutro.
 */
function AchadoAvulsoCard({
  vistoriaId,
  secaoChave,
  achado,
  fotos,
  editavel,
  aoSalvarDescricao,
  aoExcluir,
  aoEnviarFotos,
  aoExcluirFoto,
  aoAbrirFoto,
}: {
  vistoriaId: string;
  secaoChave: string;
  achado: AchadoAvulsoApi;
  fotos: FotoParaExibir[];
  editavel: boolean;
  aoSalvarDescricao: (secaoChave: string, itemChave: string, descricao: string) => void;
  aoExcluir: (itemChave: string) => void;
  aoEnviarFotos: (secaoChave: string, arquivos: FileList, itemChave?: string) => void;
  aoExcluirFoto: (fotoId: string) => void;
  aoAbrirFoto: (foto: FotoParaExibir) => void;
}) {
  const inputArquivo = useRef<HTMLInputElement>(null);

  return (
    <div className="caixa-nao-conformidade" style={{ marginTop: 8 }}>
      <div className="caixa-nao-conformidade-cabecalho">
        <span className="caixa-nao-conformidade-rotulo">Fora do checklist</span>
        {editavel && (
          <button
            type="button"
            className="botao botao-texto"
            style={{ color: "var(--erro)", padding: "2px 4px" }}
            title="Excluir"
            onClick={() => aoExcluir(achado.itemChave)}
          >
            <IconeLixeira tamanho={17} cor="var(--erro)" />
          </button>
        )}
      </div>
      <textarea
        rows={2}
        defaultValue={achado.descricao ?? ""}
        disabled={!editavel}
        enterKeyHint="done"
        placeholder="Descreva o que foi observado"
        onBlur={(e) => aoSalvarDescricao(secaoChave, achado.itemChave, e.target.value)}
      />

      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--texto-suave)", marginTop: 10 }}>
        Fotos{fotos.length > 0 ? ` (${fotos.length})` : ""}
      </div>
      <div className="grade-fotos-item">
        {fotos.map((foto) => (
          <div key={foto.id} className="foto-item-thumb">
            <img
              src={"pendente" in foto ? foto.previewLocalUrl : api.urlFoto(vistoriaId, foto.id, "miniatura")}
              alt=""
              onClick={() => aoAbrirFoto(foto)}
              style={{ opacity: "pendente" in foto ? 0.6 : 1 }}
            />
            {editavel && !("pendente" in foto) && (
              <button
                type="button"
                onClick={() => aoExcluirFoto(foto.id)}
                title="Excluir foto"
                style={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  background: "rgba(30,30,36,0.6)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  width: 18,
                  height: 18,
                  lineHeight: "16px",
                  fontSize: 11,
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            )}
          </div>
        ))}
        {editavel && (
          <>
            <input
              ref={inputArquivo}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) aoEnviarFotos(secaoChave, e.target.files, achado.itemChave);
                e.target.value = "";
              }}
            />
            <button type="button" className="foto-adicionar" onClick={() => inputArquivo.current?.click()}>
              <span style={{ fontSize: 16 }}>+</span>
              Adicionar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
